import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, ShieldCheck } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { RECEIVABLE_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission, useAuth } from '../../auth/AuthContext';

const STATUS_LABELS: Record<string, string> = { outstanding: 'Outstanding', partially_collected: 'Partially Collected', collected: 'Collected' };

export function ReceivableDetailPage() {
  const { id } = useParams<{ id: string }>();
  const receivableId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.receivables.collect');
  const { user } = useAuth();
  const [collecting, setCollecting] = useState(false);
  const [amount, setAmount] = useState('');
  const [collectionDate, setCollectionDate] = useState(new Date().toISOString().slice(0, 10));
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);
  const [periodId, setPeriodId] = useState<number | null>(null);

  const receivableQuery = useQuery({ queryKey: ['accounts_receivable', receivableId], queryFn: () => financeApi.getReceivable(receivableId) });
  const docsQuery = useQuery({ queryKey: ['receivable-documents', receivableId], queryFn: () => genericDocumentApi.listFor('accounts_receivable', receivableId) });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: collecting });
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', fiscalYearId], queryFn: () => financeApi.getFiscalYear(fiscalYearId!), enabled: !!fiscalYearId });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['accounts_receivable', receivableId] });

  const collectMutation = useMutation({
    mutationFn: () => financeApi.collectReceivable(receivableId, { amount: Number(amount), collectionDate, fiscalYearId: fiscalYearId!, accountingPeriodId: periodId! }),
    onSuccess: (result) => { showSuccess(`Collection recorded - journal ${result.data.journalReference}, now ${result.data.status}.`); invalidate(); setCollecting(false); setAmount(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record collection - check Finance Settings has the Bank and Receivable accounts configured.'),
  });

  const authorizeMutation = useMutation({
    mutationFn: () => financeApi.authorizeReceivable(receivableId),
    onSuccess: () => { showSuccess('Sent for collection authorization.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to request authorization.'),
  });

  const decideAuthMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => financeApi.decideReceivableAuthorization(receivableId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  if (!receivableQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const { data: r, collections, authorization } = receivableQuery.data;

  const noAuthorizationYet = authorization.length === 0;
  const myPendingAuth = authorization.find((a) => a.approverUserId === user?.id && a.decision === 'pending');
  const authRejected = authorization.some((a) => a.decision === 'rejected');
  const authApproved = authorization.length > 0 && authorization.every((a) => a.decision === 'approved');

  return (
    <div className="page">
      <Link to="/finance/receivables">&larr; Back to Receivables</Link>
      <RecordHeader
        title={`Receivable - ${r.receivable_reference}`}
        entityType="accounts_receivable"
        entityId={receivableId}
        fields={[
          { label: 'Donor', value: r.donor_name ?? '-' },
          { label: 'Amount', value: Number(r.amount).toLocaleString() },
          { label: 'Collected', value: Number(r.amount_collected).toLocaleString() },
          { label: 'Outstanding', value: (Number(r.amount) - Number(r.amount_collected)).toLocaleString() },
          { label: 'Status', value: <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
        ]}
        actions={
          <span className="icon-action-group">
            {r.status !== 'collected' && noAuthorizationYet && (
              <button className="btn-primary" onClick={() => authorizeMutation.mutate()} disabled={authorizeMutation.isPending}><ShieldCheck size={16} className="inline-icon" /> Request Collection Authorization</button>
            )}
            {myPendingAuth && (
              <>
                <button className="btn-primary" onClick={() => decideAuthMutation.mutate('approved')} disabled={decideAuthMutation.isPending}>Approve Authorization</button>
                <button className="button-danger" onClick={() => decideAuthMutation.mutate('rejected')} disabled={decideAuthMutation.isPending}>Reject Authorization</button>
              </>
            )}
            {r.status !== 'collected' && authApproved && (
              <button className="btn-primary" onClick={() => setCollecting((v) => !v)}><Banknote size={16} className="inline-icon" /> Record Collection</button>
            )}
          </span>
        }
        readOnly={!canManage}
      />
      {authRejected && <p className="error-text">Collection authorization was rejected for this receivable - it cannot be collected.</p>}

      {collecting && (
        <section className="card">
          <h2>Record Collection</h2>
          <label>Amount<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <label>Collection Date<input type="date" value={collectionDate} onChange={(e) => setCollectionDate(e.target.value)} /></label>
          <label>
            Fiscal Year
            <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => setPeriodId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {periods.map((per) => <option key={per.id} value={per.id}>{per.period_label}</option>)}
            </select>
          </label>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={() => collectMutation.mutate()} disabled={collectMutation.isPending || !amount || !periodId}>{collectMutation.isPending ? 'Recording…' : 'Record Collection'}</button>
            <button type="button" className="btn-secondary" onClick={() => setCollecting(false)}>Cancel</button>
          </div>
        </section>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Description</h3>
                  <p>{r.description}</p>
                  {collections && collections.length > 0 && (
                    <>
                      <h3>Collection History</h3>
                      <DataTable
                        rows={collections}
                        rowKey={(c) => c.id}
                        emptyMessage="No collections recorded yet."
                        columns={[
                          { header: 'Date', render: (c) => c.collection_date },
                          { header: 'Amount', render: (c) => Number(c.amount).toLocaleString() },
                          { header: 'Journal', render: (c) => c.journal_reference ?? '-' },
                        ]}
                      />
                    </>
                  )}
                </section>
                <CommentsThread entityType="accounts_receivable" entityId={receivableId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('accounts_receivable', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('accounts_receivable', receivableId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('accounts_receivable', documentId)}
                queryKeyToInvalidate={['receivable-documents', receivableId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="accounts_receivable" entityId={receivableId} actionStageMap={RECEIVABLE_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
